import { z } from 'zod';
import { dataModelSchema, paramFieldSchema } from './device.js';

/**
 * An ONU type is the platform's knowledge of one CPE model: where each logical
 * field lives in that model's data model, and how its WLAN instances are laid
 * out. Discovery fills these in automatically, but they are editable and
 * cloneable so a new model can inherit from a similar one and override only
 * what differs — which is how the library grows without re-deriving everything
 * per model.
 */

export const wlanBandSchema = z.enum(['2.4G', '5G', 'unknown']);

export const wlanInstanceSchema = z.object({
  /** Instance number as it appears in the data model. */
  instance: z.string().regex(/^\d+$/, 'instance must be a number'),
  /** What an operator calls it, e.g. "2.4G SSID1". */
  label: z.string().min(1).max(40),
});
export type WlanInstance = z.infer<typeof wlanInstanceSchema>;

export const onuTypeSchema = z.object({
  id: z.string(),
  manufacturer: z.string(),
  productClass: z.string(),
  /** Empty means the mapping applies to every firmware of this model. */
  softwareVersion: z.string(),
  label: z.string().nullable(),
  dataModel: dataModelSchema,
  paths: z.record(paramFieldSchema, z.string()),
  wlanMap: z.record(z.string(), z.string()).nullable(),
  /** Curated entries are never overwritten by automatic discovery. */
  curated: z.boolean(),
  /** How many devices in the fleet this type currently matches. */
  deviceCount: z.number().int().optional(),
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type OnuType = z.infer<typeof onuTypeSchema>;

export const createOnuTypeSchema = z.object({
  manufacturer: z.string().min(1).max(80),
  productClass: z.string().min(1).max(80),
  softwareVersion: z.string().max(80).default(''),
  label: z.string().max(120).optional(),
  dataModel: dataModelSchema.default('unknown'),
  paths: z.record(paramFieldSchema, z.string().max(400)).default({}),
  wlanMap: z.record(z.string(), z.string().max(40)).nullable().optional(),
  curated: z.boolean().default(true),
});
export type CreateOnuTypeRequest = z.infer<typeof createOnuTypeSchema>;

export const updateOnuTypeSchema = createOnuTypeSchema.partial().omit({
  manufacturer: true,
  productClass: true,
});
export type UpdateOnuTypeRequest = z.infer<typeof updateOnuTypeSchema>;

/**
 * Clone an existing type onto another model. `overrides` replace individual
 * paths; everything else is inherited, which is the point — two models from the
 * same vendor usually differ in one or two places, not everywhere.
 */
export const cloneOnuTypeSchema = z.object({
  manufacturer: z.string().min(1).max(80),
  productClass: z.string().min(1).max(80),
  softwareVersion: z.string().max(80).default(''),
  label: z.string().max(120).optional(),
  overrides: z.record(paramFieldSchema, z.string().max(400)).default({}),
  wlanMap: z.record(z.string(), z.string().max(40)).nullable().optional(),
});
export type CloneOnuTypeRequest = z.infer<typeof cloneOnuTypeSchema>;

/**
 * A model seen informing that has no type yet. Surfacing these is what turns
 * the library from something to maintain into something that fills itself.
 */
export const unmappedModelSchema = z.object({
  manufacturer: z.string(),
  productClass: z.string(),
  model: z.string().nullable(),
  deviceCount: z.number().int(),
  /** A device of this model, so a new type can be discovered from it. */
  sampleDeviceId: z.string(),
  softwareVersions: z.array(z.string()),
});
export type UnmappedModel = z.infer<typeof unmappedModelSchema>;
